import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'

import { ErroDeAutenticacao } from '@/auth/adaptadores/tipos'
import { destinoPadrao, type EstadoDeRedirecionamento } from '@/auth/destino'
import {
  CODIGO_EXPIRADO_MOCK,
  CODIGO_REDEFINICAO_MOCK,
  FIXTURES_DE_SESSAO,
  SENHA_MOCK,
  SENHA_TEMPORARIA_MOCK,
} from '@/auth/fixtures/sessoes'
import {
  MENSAGEM_SESSAO_EXPIRADA,
  mensagemDeErroDeLogin,
  mensagemDeErroDeRedefinicao,
} from '@/auth/mensagensDeErro'
import {
  avaliarSenha,
  POLITICA_PADRAO,
  senhaAtendePolitica,
  type PoliticaDeSenha,
} from '@/auth/politicaDeSenha'
import { useSession } from '@/auth/session'
import { TelaDeEspera } from '@/auth/TelaDeEspera'
import logoIoxCloud from '@/styles/design-system/assets/iox-cloud-logo.svg'

/**
 * Login — shot `1b`, plus the first-access step the shot does not draw.
 *
 * Deliberately outside the app shell: no sidebar, no module frame, no client selector
 * before authentication.
 *
 * ⛔ **BR-08 / BR-09 — three things this screen must never grow:**
 * a sign-up link, a social/federated button, and an SSO provider list. Access exists
 * only because an Administrador issued an invitation. UI kits reintroduce all three
 * by themselves, which is why `LoginPage.test.tsx` asserts their absence rather than
 * trusting a comment.
 *
 * **`esqueci minha senha` is drawn by `1b` and IS built** — the PO confirmed on
 * 2026-08-14 that a user may reset their own password. It shipped omitted in story 0.3
 * only because the answer had not arrived yet; a link to an undefined flow is worse
 * than no link. **BR-08 was never in tension with it: that rule forbids
 * self-REGISTRATION, not self-RECOVERY.**
 *
 * **First access IS built** (decided by Fábio, 2026-08-13). It is a STATE of this
 * screen rather than a route, because the challenge lives in the adapter's memory
 * between `entrar()` and `definirNovaSenha()` — a bookmarkable `/definir-senha` would
 * be dead on arrival after a reload.
 *
 * The two reset steps are states of this screen for a related but different reason:
 * their only inputs are an e-mail and a code, and putting an e-mail in a URL to make
 * the step bookmarkable would leak it into history and referrers to buy nothing. A
 * reload costs the form, never the code — that is still in the user's inbox.
 *
 * ⚠️ **Neither the first-access step nor either reset step is drawn in the handoff.**
 * All three follow `1b`'s right-hand column and add nothing decorative. Show them at
 * the next alinhamento round (§9).
 */

/**
 * Which form the screen shows. `desafio` from the provider outranks this: a server
 * challenge is not a step the user chose to be on.
 */
type PassoDeLogin = 'credenciais' | 'pedirCodigo' | 'novaSenhaPorCodigo'

export function LoginPage() {
  const { sessao, carregando, desafio } = useSession()
  const location = useLocation()

  const [passo, setPasso] = useState<PassoDeLogin>('credenciais')
  /** Threaded through both reset steps: `confirmResetPassword` needs the username. */
  const [emailDeRedefinicao, setEmailDeRedefinicao] = useState('')
  const [sucesso, setSucesso] = useState<string | null>(null)

  const destinoGuardado = (location.state as EstadoDeRedirecionamento | null)?.de

  if (carregando) return <TelaDeEspera />

  // Already signed in — nobody should sit on a login form they do not need.
  if (sessao) return <Navigate to={destinoGuardado ?? destinoPadrao(sessao)} replace />

  function voltarAoLogin(mensagemDeSucesso: string | null = null) {
    setPasso('credenciais')
    setSucesso(mensagemDeSucesso)
  }

  return (
    <main className="bg-page grid min-h-dvh lg:grid-cols-2">
      <PainelDeMarca />
      <section className="flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-md">{conteudo()}</div>
      </section>
    </main>
  )

  function conteudo() {
    // A challenge opened by `entrar()` wins over any step the user navigated to.
    if (desafio === 'novaSenha') return <FormularioDeNovaSenha />

    if (passo === 'pedirCodigo') {
      return (
        <FormularioDePedidoDeCodigo
          emailInicial={emailDeRedefinicao}
          aoEnviar={(email) => {
            setEmailDeRedefinicao(email)
            setPasso('novaSenhaPorCodigo')
          }}
          aoVoltar={() => voltarAoLogin()}
        />
      )
    }

    if (passo === 'novaSenhaPorCodigo') {
      return (
        <FormularioDeRedefinicao
          email={emailDeRedefinicao}
          aoConcluir={() => voltarAoLogin('Senha redefinida. Entre com a nova senha.')}
          aoVoltar={() => setPasso('pedirCodigo')}
        />
      )
    }

    return (
      <FormularioDeCredenciais
        sucesso={sucesso}
        aoEsquecerSenha={() => {
          setSucesso(null)
          setPasso('pedirCodigo')
        }}
      />
    )
  }
}

// ─── Passo 1: credenciais ──────────────────────────────────────────────────────

interface FormularioDeCredenciaisProps {
  /** Set after a reset completes — the pool leaves the user signed out. */
  sucesso: string | null
  aoEsquecerSenha: () => void
}

function FormularioDeCredenciais({ sucesso, aoEsquecerSenha }: FormularioDeCredenciaisProps) {
  const { entrar, avisoDeSaida, descartarAviso, modo } = useSession()

  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  async function aoEnviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (enviando) return

    // Validated here rather than by the browser: native constraint bubbles are
    // rendered in the BROWSER's language, and every string on this screen is pt-BR.
    if (!email.trim()) return setErro(mensagemDeErroDeLogin('EmptySignInUsername'))
    if (!senha) return setErro(mensagemDeErroDeLogin('EmptySignInPassword'))

    setErro(null)
    setEnviando(true)
    descartarAviso()

    try {
      await entrar(email, senha)
      // No navigation here: either `sessao` becomes non-null and `LoginPage`
      // redirects, or `desafio` opens and this form is replaced by the next step.
    } catch (falha) {
      setErro(mensagemDeErro(falha))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <>
      <h1 className="type-h2 text-text-primary">Entrar na plataforma</h1>

      {modo === 'mock' && <AvisoDeModoMockado />}

      {sucesso && <Aviso tom="sucesso">{sucesso}</Aviso>}

      {avisoDeSaida === 'expirada' && <Aviso tom="atencao">{MENSAGEM_SESSAO_EXPIRADA}</Aviso>}

      <form onSubmit={aoEnviar} noValidate className="mt-8 flex flex-col gap-6">
        <Campo
          id="email"
          rotulo="E-mail"
          icone="ph-envelope-simple"
          tipo="email"
          autoComplete="email"
          placeholder="voce@orgao.jus.br"
          valor={email}
          aoMudar={setEmail}
        />

        <Campo
          id="senha"
          rotulo="Senha"
          icone="ph-lock-simple"
          tipo="password"
          autoComplete="current-password"
          placeholder="••••••••"
          valor={senha}
          aoMudar={setSenha}
        />

        {erro && <Aviso tom="erro">{erro}</Aviso>}

        <BotaoPrimario enviando={enviando} rotulo="entrar" rotuloEnviando="entrando…" />
      </form>

      {/* Posição do shot `1b`: abaixo do botão, alinhado à esquerda, antes da copy de
          convite. É <button> e não <a> porque o passo é estado desta tela, não rota —
          um href para lugar nenhum quebra ctrl+clique e leitor de tela. */}
      <button
        type="button"
        onClick={aoEsquecerSenha}
        className="text-text-link hover:text-iox-teal mt-6 type-button transition-colors duration-base ease-out"
      >
        esqueci minha senha
      </button>

      {/* Copy do shot `1b`, verbatim — é a explicação de por que não há cadastro. */}
      <aside className="rounded-field bg-subtle text-text-body mt-10 flex gap-3 px-4 py-4 type-sm">
        <i className="ph ph-info text-iox-navy mt-0.5 shrink-0 text-lg" aria-hidden="true" />
        <p>
          O acesso é criado <strong>somente por convite</strong> do administrador. Não há
          autocadastro nem login federado (SSO).
        </p>
      </aside>
    </>
  )
}

// ─── Passo 2: primeiro acesso ──────────────────────────────────────────────────

/**
 * `FORCE_CHANGE_PASSWORD` — the state Cognito puts EVERY invited user in.
 *
 * Not drawn in the handoff (§9 records that), so the layout follows `1b`'s right-hand
 * column exactly and adds nothing decorative.
 *
 * The requirement list is rendered from the pool's own policy and ticks live, rather
 * than rejecting one rule at a time — a password refused rule-by-rule is a password
 * typed four times. It stays a **courtesy** check: Cognito is authoritative, and its
 * `InvalidPasswordException` surfaces in the same error slot.
 */
function FormularioDeNovaSenha() {
  const { definirNovaSenha, cancelarDesafio } = useSession()

  const politica = usePoliticaDeSenha()
  const [senha, setSenha] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  async function aoEnviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (enviando) return

    const local = validarNovaSenha(senha, confirmacao, politica)
    if (local) return setErro(local)

    setErro(null)
    setEnviando(true)

    try {
      await definirNovaSenha(senha)
    } catch (falha) {
      setErro(mensagemDeErro(falha))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <>
      <h1 className="type-h2 text-text-primary">Defina sua senha</h1>
      <p className="text-text-body mt-3 type-body">
        Este é o seu primeiro acesso. Escolha uma senha para substituir a que o administrador enviou
        no convite.
      </p>

      <form onSubmit={aoEnviar} noValidate className="mt-8 flex flex-col gap-6">
        <CamposDeNovaSenha
          politica={politica}
          senha={senha}
          aoMudarSenha={setSenha}
          confirmacao={confirmacao}
          aoMudarConfirmacao={setConfirmacao}
        />

        {erro && <Aviso tom="erro">{erro}</Aviso>}

        <div className="flex items-center gap-4">
          <BotaoPrimario
            enviando={enviando}
            rotulo="definir senha e entrar"
            rotuloEnviando="definindo…"
          />
          <BotaoSecundario onClick={cancelarDesafio} rotulo="cancelar" />
        </div>
      </form>
    </>
  )
}

// ─── Redefinição de senha, passo 1: pedir o código ─────────────────────────────

/**
 * Asks the pool to e-mail a code.
 *
 * ⚠️ **This step advances on success AND on "no such account", by design.** The
 * adapters swallow `UserNotFoundException` precisely so this screen cannot be used to
 * find out who has access to an invitation-only platform (BR-08). It follows that the
 * screen must not promise the e-mail was sent either — the copy says *if* an account
 * exists. Do not "improve" this into a confirmation that the address was found.
 */
function FormularioDePedidoDeCodigo({
  emailInicial,
  aoEnviar,
  aoVoltar,
}: {
  emailInicial: string
  aoEnviar: (email: string) => void
  aoVoltar: () => void
}) {
  const { solicitarCodigoDeRedefinicao } = useSession()

  const [email, setEmail] = useState(emailInicial)
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  async function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (enviando) return

    if (!email.trim()) return setErro(mensagemDeErroDeLogin('EmptySignInUsername'))

    setErro(null)
    setEnviando(true)

    try {
      await solicitarCodigoDeRedefinicao(email)
      aoEnviar(email.trim())
    } catch (falha) {
      // Only genuine failures reach here — throttling, delivery problems, and the
      // invited account still in FORCE_CHANGE_PASSWORD. An unknown address resolves
      // silently one layer down.
      setErro(mensagemDeErroDeReset(falha))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <>
      <h1 className="type-h2 text-text-primary">Redefinir senha</h1>
      <p className="text-text-body mt-3 type-body">
        Informe o e-mail do seu acesso. Se houver uma conta com esse endereço, enviaremos um código
        de confirmação.
      </p>

      <form onSubmit={enviar} noValidate className="mt-8 flex flex-col gap-6">
        <Campo
          id="email"
          rotulo="E-mail"
          icone="ph-envelope-simple"
          tipo="email"
          autoComplete="email"
          placeholder="voce@orgao.jus.br"
          valor={email}
          aoMudar={setEmail}
        />

        {erro && <Aviso tom="erro">{erro}</Aviso>}

        <div className="flex items-center gap-4">
          <BotaoPrimario
            enviando={enviando}
            rotulo="enviar código"
            rotuloEnviando="enviando código…"
          />
          <BotaoSecundario onClick={aoVoltar} rotulo="voltar para o login" />
        </div>
      </form>
    </>
  )
}

// ─── Redefinição de senha, passo 2: código + nova senha ────────────────────────

/**
 * Finishes the reset.
 *
 * Unlike first access, a successful call here does **not** produce a session —
 * `confirmResetPassword` leaves the user signed out, so the screen returns to the
 * credentials form with a notice instead of pretending to authenticate. Replaying the
 * password into `entrar()` to smooth that over would mean holding a plaintext
 * credential to save one form submit.
 */
function FormularioDeRedefinicao({
  email,
  aoConcluir,
  aoVoltar,
}: {
  email: string
  aoConcluir: () => void
  aoVoltar: () => void
}) {
  const { confirmarRedefinicaoDeSenha } = useSession()

  const politica = usePoliticaDeSenha()
  const [codigo, setCodigo] = useState('')
  const [senha, setSenha] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  async function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (enviando) return

    if (!codigo.trim()) return setErro('Informe o código que enviamos para o seu e-mail.')

    // Checked before the round-trip: a weak password rejected by the pool would burn
    // the code and send the user back for a new e-mail.
    const local = validarNovaSenha(senha, confirmacao, politica)
    if (local) return setErro(local)

    setErro(null)
    setEnviando(true)

    try {
      await confirmarRedefinicaoDeSenha(email, codigo.trim(), senha)
      aoConcluir()
    } catch (falha) {
      setErro(mensagemDeErro(falha))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <>
      <h1 className="type-h2 text-text-primary">Escolha uma nova senha</h1>
      <p className="text-text-body mt-3 type-body">
        Enviamos um código para <strong>{email}</strong>. Informe o código e escolha a nova senha.
      </p>

      <form onSubmit={enviar} noValidate className="mt-8 flex flex-col gap-6">
        <Campo
          id="codigo"
          rotulo="Código"
          icone="ph-hash"
          tipo="text"
          autoComplete="one-time-code"
          placeholder="123456"
          valor={codigo}
          aoMudar={setCodigo}
        />

        <CamposDeNovaSenha
          politica={politica}
          senha={senha}
          aoMudarSenha={setSenha}
          confirmacao={confirmacao}
          aoMudarConfirmacao={setConfirmacao}
        />

        {erro && <Aviso tom="erro">{erro}</Aviso>}

        <div className="flex items-center gap-4">
          <BotaoPrimario
            enviando={enviando}
            rotulo="redefinir senha"
            rotuloEnviando="redefinindo…"
          />
          {/* Um código expirado só se resolve pedindo outro — este é o caminho. */}
          <BotaoSecundario onClick={aoVoltar} rotulo="voltar" />
        </div>
      </form>
    </>
  )
}

// ─── Peças compartilhadas ──────────────────────────────────────────────────────

/**
 * The pool's password policy, or Cognito's defaults while it loads.
 *
 * Falling back beats blocking: these screens are the only way a new user gets in, and
 * the pool refuses anything genuinely invalid regardless of what was listed here.
 */
function usePoliticaDeSenha(): PoliticaDeSenha {
  const { politicaDeSenha } = useSession()
  const [politica, setPolitica] = useState<PoliticaDeSenha>(POLITICA_PADRAO)

  useEffect(() => {
    let vivo = true
    void politicaDeSenha()
      .then((carregada) => {
        if (vivo) setPolitica(carregada)
      })
      .catch(() => undefined)
    return () => {
      vivo = false
    }
  }, [politicaDeSenha])

  return politica
}

/**
 * The local checks both password screens run before spending a round-trip.
 *
 * Returns the pt-BR complaint, or `null` when the pair is acceptable. It stays a
 * **courtesy**: Cognito is authoritative and its `InvalidPasswordException` lands in
 * the same error slot.
 */
function validarNovaSenha(
  senha: string,
  confirmacao: string,
  politica: PoliticaDeSenha,
): string | null {
  if (!senhaAtendePolitica(senha, politica)) {
    return 'A senha ainda não atende a todos os requisitos abaixo.'
  }
  if (senha !== confirmacao) {
    return 'A confirmação não confere com a senha digitada.'
  }
  return null
}

/**
 * New password + live requirement list + confirmation.
 *
 * Shared by first access and by reset: same rules, same copy, same ticking list. Two
 * copies of this would drift the day the policy changes.
 */
function CamposDeNovaSenha({
  politica,
  senha,
  aoMudarSenha,
  confirmacao,
  aoMudarConfirmacao,
}: {
  politica: PoliticaDeSenha
  senha: string
  aoMudarSenha: (valor: string) => void
  confirmacao: string
  aoMudarConfirmacao: (valor: string) => void
}) {
  const requisitos = avaliarSenha(senha, politica)

  return (
    <>
      <Campo
        id="nova-senha"
        rotulo="Nova senha"
        icone="ph-lock-key"
        tipo="password"
        autoComplete="new-password"
        placeholder="••••••••"
        valor={senha}
        aoMudar={aoMudarSenha}
        rotuloDeRevelacao="Mostrar a nova senha"
      />

      {/* A lista inteira aparece desde o início e marca ao vivo: uma senha recusada
          um requisito por vez é uma senha digitada quatro vezes. */}
      <ul className="flex flex-col gap-1.5" aria-label="Requisitos da senha">
        {requisitos.map((requisito) => (
          <li
            key={requisito.id}
            className={`flex items-center gap-2 type-sm ${
              requisito.atendido ? 'text-success-700' : 'text-text-muted'
            }`}
          >
            <i
              className={`ph ${requisito.atendido ? 'ph-check-circle' : 'ph-circle'} text-base`}
              aria-hidden="true"
            />
            {requisito.texto}
            {/* O ícone é decorativo; o estado precisa chegar a leitor de tela. */}
            <span className="sr-only">{requisito.atendido ? '(atendido)' : '(pendente)'}</span>
          </li>
        ))}
      </ul>

      <Campo
        id="confirmacao-senha"
        rotulo="Confirme a nova senha"
        icone="ph-lock-key"
        tipo="password"
        autoComplete="new-password"
        placeholder="••••••••"
        valor={confirmacao}
        aoMudar={aoMudarConfirmacao}
        rotuloDeRevelacao="Mostrar a confirmação da senha"
      />
    </>
  )
}

/** The `cancelar` / `voltar` affordance next to a primary action. */
function BotaoSecundario({ onClick, rotulo }: { onClick: () => void; rotulo: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-text-link hover:text-iox-teal type-button transition-colors duration-base ease-out"
    >
      {rotulo}
    </button>
  )
}

/** Any failure that is not `ErroDeAutenticacao` gets the generic pt-BR sentence. */
function mensagemDeErro(falha: unknown): string {
  return mensagemDeErroDeLogin(falha instanceof ErroDeAutenticacao ? falha.codigo : 'Desconhecido')
}

/** O mesmo, para o passo de pedir o código — onde `NotAuthorizedException` diz outra coisa. */
function mensagemDeErroDeReset(falha: unknown): string {
  return mensagemDeErroDeRedefinicao(
    falha instanceof ErroDeAutenticacao ? falha.codigo : 'Desconhecido',
  )
}

/** Left half of `1b`: dark panel, logo, claim, region footnote. */
function PainelDeMarca() {
  return (
    <aside className="hidden flex-col justify-between bg-[image:var(--gradient-panel)] p-16 text-white lg:flex">
      {/* O logo nunca é redesenhado; sobre painel escuro o handoff pede
          `brightness(0) invert(1)`. */}
      <img
        src={logoIoxCloud}
        alt="IOX Cloud"
        className="h-14 w-auto self-start [filter:brightness(0)_invert(1)]"
      />

      <div className="max-w-lg">
        <p className="text-iox-cyan type-eyebrow">Plataforma de gestão</p>
        <p className="mt-6 type-h1">
          FinOps, orçamento e <span className="text-iox-cyan">conformidade</span> em um só lugar.
        </p>
        <p className="mt-6 text-white/70 type-body-lg">
          Acoplada ao serviço de advisory da IOX Cloud.
        </p>
      </div>

      <p className="text-white/50 type-sm">AWS · Região São Paulo (sa-east-1)</p>
    </aside>
  )
}

const ESTILO_DE_AVISO = {
  erro: 'border-danger-500 bg-danger-100 text-danger-700',
  atencao: 'border-warning-500 bg-warning-100 text-text-body',
  sucesso: 'border-success-500 bg-success-100 text-success-700',
} as const

function Aviso({ tom, children }: { tom: keyof typeof ESTILO_DE_AVISO; children: ReactNode }) {
  return (
    <p
      // `alert` interrupts, `status` waits its turn. A failure the user must fix
      // interrupts; a confirmation does not.
      role={tom === 'erro' ? 'alert' : 'status'}
      className={`rounded-field border-l-4 px-4 py-3 type-sm ${ESTILO_DE_AVISO[tom]} ${
        tom === 'erro' ? '' : 'mt-6'
      }`}
    >
      {children}
    </p>
  )
}

function BotaoPrimario({
  enviando,
  rotulo,
  rotuloEnviando,
}: {
  enviando: boolean
  rotulo: string
  rotuloEnviando: string
}) {
  return (
    <button
      type="submit"
      disabled={enviando}
      className="rounded-control bg-action-primary text-action-primary-fg hover:bg-action-primary-hover disabled:bg-action-disabled disabled:text-action-disabled-fg self-start px-8 py-3.5 type-button transition-colors duration-base ease-out"
    >
      {enviando ? rotuloEnviando : rotulo}
    </button>
  )
}

/**
 * Mock mode is a bypassable login. It is correct while the pool does not exist and
 * catastrophic if it ever reaches an environment anyone else can open — so it says so
 * on screen, loudly, rather than in a console warning nobody reads.
 */
function AvisoDeModoMockado() {
  return (
    <div className="rounded-field border-warning-500 bg-warning-100 text-text-body mt-6 border-l-4 px-4 py-3">
      <p className="type-label">Modo mockado — autenticação desativada</p>
      <p className="mt-2 type-sm">
        O pool do Cognito só existe a partir da linha <strong>CONEXÃO</strong> (14/ago). Senha das
        personas já ativas: <code className="type-label">{SENHA_MOCK}</code>
      </p>
      <ul className="mt-2 flex flex-col gap-1 type-sm">
        {FIXTURES_DE_SESSAO.map((fixture) => (
          <li key={fixture.sub}>
            <strong>{fixture.rotulo}</strong> — {fixture.email}
            {fixture.primeiroAcesso && (
              <>
                {' '}
                · senha temporária <code className="type-label">{SENHA_TEMPORARIA_MOCK}</code>, cai
                na tela de definir senha
              </>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-2 type-sm">
        Redefinição de senha: código <code className="type-label">{CODIGO_REDEFINICAO_MOCK}</code>.
        Use <code className="type-label">{CODIGO_EXPIRADO_MOCK}</code> para ver o caminho do código
        expirado.
      </p>
    </div>
  )
}

interface CampoProps {
  id: string
  rotulo: string
  /** Phosphor class, per the handoff's icon set. */
  icone: string
  tipo: 'email' | 'password' | 'text'
  autoComplete: string
  placeholder: string
  valor: string
  aoMudar: (valor: string) => void
  /**
   * Rótulo do botão do olho. Específico por campo, e não um "Mostrar senha"
   * genérico: na tela de primeiro acesso são DOIS campos de senha lado a lado, e
   * dois botões com o mesmo nome são indistinguíveis num leitor de tela — o
   * mesmo motivo do `rotuloDeRemocao` do `Chip`.
   */
  rotuloDeRevelacao?: string
}

/**
 * Um campo do formulário.
 *
 * **Todo campo de senha ganha o olho de "mostrar senha"** — o de entrar, o de
 * primeiro acesso e o de confirmação. Não está desenhado no shot `1b`; é adição
 * nossa, pedida pelo Fábio, e vale mais aqui do que em qualquer outro lugar do
 * produto: a política do pool exige maiúscula, número e símbolo, e a lista de
 * requisitos que fica marcando ao vivo logo abaixo do campo é ilegível se o
 * usuário não pode conferir o que digitou. **Mostrar no próximo alinhamento com
 * o Eduardo** (§9), junto com os outros passos que o handoff não desenha.
 *
 * O botão fica DENTRO do campo, à direita, e o `pr` do input abre espaço para
 * ele — sem isso o texto passa por baixo do ícone numa senha longa.
 */
function Campo({
  id,
  rotulo,
  icone,
  tipo,
  autoComplete,
  placeholder,
  valor,
  aoMudar,
  rotuloDeRevelacao = 'Mostrar senha',
}: CampoProps) {
  const [revelada, setRevelada] = useState(false)

  const revelavel = tipo === 'password'
  // ⚠️ O `type` real, e não `tipo`. É o que o gerenciador de senhas lê para
  // decidir se preenche o campo, e é o que `LoginPage.test.tsx` afirma sobre o
  // estado inicial: um campo de senha nasce mascarado, sempre.
  const tipoDoInput = revelavel && revelada ? 'text' : tipo

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-text-primary type-label">
        {rotulo}
      </label>
      <div className="relative flex items-center">
        <i
          className={`ph ${icone} text-icon-muted pointer-events-none absolute left-4 text-lg`}
          aria-hidden="true"
        />
        <input
          id={id}
          name={id}
          type={tipoDoInput}
          autoComplete={autoComplete}
          placeholder={placeholder}
          value={valor}
          onChange={(evento) => aoMudar(evento.target.value)}
          /* Sem `outline-none` e sem `focus:ring-*`: o halo cyan de 3px vem de
             `base.css` (`*:focus-visible`) e o handoff diz que nunca é removido. */
          className={`rounded-field border-border-subtle bg-surface-card text-text-body placeholder:text-text-muted w-full border py-3.5 pl-11 type-body ${
            revelavel ? 'pr-12' : 'pr-4'
          }`}
        />
        {revelavel && (
          <button
            type="button"
            // `type="button"` é obrigatório: dentro de um <form>, um <button>
            // sem type é submit, e o olho enviaria o login.
            onClick={() => setRevelada((atual) => !atual)}
            // O rótulo diz a AÇÃO e `aria-pressed` diz o ESTADO. Só o rótulo
            // mudando já funcionaria, mas o par é o que faz o leitor de tela
            // anunciar "mostrar senha, pressionado" em vez de trocar o nome do
            // botão debaixo do foco do usuário.
            aria-label={rotuloDeRevelacao}
            aria-pressed={revelada}
            aria-controls={id}
            title={revelada ? 'ocultar senha' : 'mostrar senha'}
            // `tabIndex={-1}` seria mais discreto no Tab entre e-mail e senha,
            // e está fora de questão: sem foco, quem navega por teclado não
            // alcança o botão.
            className="text-icon-muted hover:text-iox-teal absolute right-3 flex items-center p-1 transition-colors duration-base ease-out"
          >
            <i
              className={`ph ${revelada ? 'ph-eye-slash' : 'ph-eye'} text-lg`}
              aria-hidden="true"
            />
          </button>
        )}
      </div>
    </div>
  )
}
